import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Star, Send, MessageSquare, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Feedback = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error('Veuillez entrer votre message');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('feedback')
        .insert({
          name: name.trim() || null,
          email: email.trim() || null,
          message: message.trim(),
          rating: rating || null,
          page_url: window.location.href,
        });

      if (error) throw error;

      setIsSuccess(true);
      toast.success('Merci pour votre feedback!');
      
      // Reset form after delay
      setTimeout(() => {
        setName('');
        setEmail('');
        setMessage('');
        setRating(0);
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Erreur lors de l\'envoi du feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12 animate-fade-in">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-primary animate-fade-in animation-delay-100" />
            <h1 className="text-4xl font-bold mb-4 animate-fade-in animation-delay-200">Votre Avis Compte</h1>
            <p className="text-muted-foreground text-lg animate-fade-in animation-delay-300">
              Aidez-nous à améliorer BrazzaBoost en partageant votre expérience
            </p>
          </div>

          {isSuccess ? (
            <Card className="animate-fade-in">
              <CardContent className="p-12 text-center">
                <CheckCircle2 className="h-16 w-16 text-success mx-auto mb-4 animate-scale-in" />
                <h3 className="text-2xl font-bold mb-2">Merci!</h3>
                <p className="text-muted-foreground">
                  Votre feedback a été envoyé avec succès. Nous l'examinerons attentivement.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="animate-fade-in animation-delay-300"
            >
              <CardHeader>
                <CardTitle>Partagez votre feedback</CardTitle>
                <CardDescription>
                  Vos commentaires nous aident à améliorer nos services. Tous les champs sont optionnels sauf le message.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Rating */}
                  <div className="space-y-2">
                    <Label>Évaluation (optionnel)</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          className="transition-all hover:scale-125 duration-200"
                        >
                          <Star
                            className={`h-8 w-8 transition-colors ${
                              star <= (hoveredRating || rating)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom (optionnel)</Label>
                    <Input
                      id="name"
                      placeholder="Votre nom"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={100}
                      className="transition-all focus:scale-[1.01]"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email (optionnel)</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={255}
                      className="transition-all focus:scale-[1.01]"
                    />
                    <p className="text-sm text-muted-foreground">
                      Si vous souhaitez recevoir une réponse
                    </p>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">
                      Message <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Partagez votre expérience, suggestions ou problèmes rencontrés..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      maxLength={2000}
                      required
                      className="transition-all focus:scale-[1.01]"
                    />
                    <p className="text-sm text-muted-foreground text-right">
                      {message.length}/2000
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full group"
                    size="lg"
                    disabled={isSubmitting || !message.trim()}
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        Envoyer le Feedback
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Info Section */}
          <div className="mt-8 p-6 bg-muted rounded-lg animate-fade-in animation-delay-300 hover-lift">
            <h3 className="font-semibold mb-2">Pourquoi votre avis est important?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Nous aide à identifier les problèmes techniques</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Nous permet d'améliorer l'expérience utilisateur</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Guide le développement de nouvelles fonctionnalités</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Renforce la communauté BrazzaBoost</span>
              </li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Feedback;
